import { checkShouldUseMocks } from '@telialt/utils/src/envUtils';
import { MS_URL } from '@telialt/utils/src/microservices';

export enum SearchType {
    All = 'visi',
    Products = 'prekes',
    Services = 'paslaugos',
    Help = 'pagalba',
    Content = 'kita',
}

export const SEARCH_QUERY_KEYS = {
    QUERY_KEYS: 'query-keys',
    SEARCH_SUGGESTION: 'search-suggestions',
    SEARCH_RESULTS_PRODUCTS: 'search-results-products',
    SEARCH_RESULTS_PRODUCTS_CUSTOM_QUERY: 'search-results-products-custom-query',
    SEARCH_RESULTS_SERVICES: 'search-results-services',
    SEARCH_RESULTS_HELP: 'search-results-help',
    SEARCH_RESULTS_CONTENT: 'search-results-content',
    SEARCH_RESULTS_CATEGORY_PRODUCTS: 'search-results-category-products',
    SEARCH_RESULTS_COMPARE_PRODUCTS: 'search-results-compare-products',
    NO_SUGGESTIONS_RECOMMENDATIONS: 'search-no-suggestions-recommendations',
    SEARCH_REDIRECTIONS: 'search-redirections',
};

export const SEARCH_INPUT_DROPDOWN_STATE_KEY = 'search-input-dropdown';
export const SEARCH_SESSION_ID_KEY = 'search-session';
export const SEARCH_TYPE_QUERY_PARAM = 'paieskos-tipas';
export const SEARCH_PAGE_QUERY_PARAM = 'puslapis';
export const SEARCH_KEYWORD_QUERY_PARAM = 'ieskoti';
export const SEARCH_QUERY_MIN_LENGTH = 2;
export const SEARCH_AUTOCOMPLETE_SUGGESTIONS_COUNT = 5;
export const SEARCH_PRODUCTS_SUGGESTIONS_COUNT = 4;
export const SEARCH_SERVICES_SUGGESTIONS_COUNT = 4;
export const SEARCH_HELP_SUGGESTIONS_COUNT = 4;
export const SEARCH_CONTENT_SUGGESTIONS_COUNT = 6;
export const SEARCH_SPECIAL_OFFERS_SUGGESTIONS_COUNT = 4;
export const SEARCH_ALL_RESULTS_PRODUCTS_COUNT = 8;
export const SEARCH_ALL_RESULTS_SERVICES_COUNT = 6;
export const SEARCH_ALL_RESULTS_HELP_COUNT = 6;
export const SEARCH_ALL_RESULTS_CONTENT_COUNT = 6;
export const SEARCH_RESULTS_SPECIAL_OFFERS_PRODUCTS_COUNT = 4;
export const SEARCH_RESULTS_SPECIAL_OFFERS_SERVICES_COUNT = 4;

export const SEARCH_RESULTS_PER_PAGE_SIZES = [12, 24, 32, 48];
export const SEARCH_RESULTS_PER_PAGE_DEFAULT_SIZE = 12;

export const SEARCH_PRODUCTS_RESULTS_PER_PAGE_COUNT = 12;
export const SEARCH_CATEGORY_PRODUCTS_RESULTS_PER_PAGE_COUNT = 18;
export const SEARCH_SERVICES_RESULTS_PER_PAGE_COUNT = 9;
export const SEARCH_HELP_RESULTS_PER_PAGE_COUNT = 18;
export const SEARCH_CONTENT_RESULTS_PER_PAGE_COUNT = 18;

export const SEARCH_QUERY_BASE_PATH = 'https://api.lupasearch.com';

export const API = {
    URL_SEARCH_QUERY_KEYS: `${MS_URL}/api/v3/search/query-keys`,
    URL_SEARCH_QUERY: `${SEARCH_QUERY_BASE_PATH}/v1/query/{queryKey}`,
    URL_REDIRECTIONS: `${SEARCH_QUERY_BASE_PATH}/v1/redirections/{queryKey}`,
    URL_SEARCH_EVENTS: `${SEARCH_QUERY_BASE_PATH}/v1/events`,
    URL_FALLBACK_SEARCH_PRODUCTS_QUERY: `${MS_URL}/api/v3/search/query`,
};

if (checkShouldUseMocks({ shouldUseInTest: true })) {
    API.URL_SEARCH_QUERY_KEYS = '/api/search/query-keys';
    API.URL_SEARCH_QUERY = '/api/search/query/{queryKey}';
    API.URL_REDIRECTIONS = '/api/search/redirections';
    API.URL_SEARCH_EVENTS = '/api/search/events';
    API.URL_FALLBACK_SEARCH_PRODUCTS_QUERY = '/api/search/b2c-products';
}

export enum SortOrder {
    Asc = 'asc',
    Desc = 'desc',
}

const colorSequence = { colorSequence: SortOrder.Asc };

export const SORT_DROPDOWN_OPTIONS = [
    {
        LABEL: 'search.results.sort.recommended',
        VALUE: 'recommended-default',
        SORT_ARRAY: undefined,
    },
    {
        LABEL: 'search.results.sort.price.ascending',
        VALUE: 'price-ascending',
        SORT_ARRAY: [{ stockStatus: SortOrder.Desc }, { fullPrice: SortOrder.Asc }, colorSequence],
    },
    {
        LABEL: 'search.results.sort.price.descending',
        VALUE: 'price-descending',
        SORT_ARRAY: [{ stockStatus: SortOrder.Desc }, { fullPrice: SortOrder.Desc }, colorSequence],
    },
    {
        LABEL: 'search.results.sort.newest',
        VALUE: 'newest',
        SORT_ARRAY: [
            { stockStatus: SortOrder.Desc },
            { effectiveDate: SortOrder.Desc },
            { fullPrice: SortOrder.Asc },
            colorSequence,
        ],
    },
    {
        LABEL: 'search.results.sort.alphabethical.ascending',
        VALUE: 'alphabetical-ascending',
        SORT_ARRAY: [
            { stockStatus: SortOrder.Desc },
            { name: SortOrder.Asc },
            { fullPrice: SortOrder.Asc },
            colorSequence,
        ],
    },
    {
        LABEL: 'search.results.sort.alphabethical.descending',
        VALUE: 'alphabetical-descending',
        SORT_ARRAY: [
            { stockStatus: SortOrder.Desc },
            { name: SortOrder.Desc },
            { fullPrice: SortOrder.Asc },
            colorSequence,
        ],
    },
];

export const URL_SORT_PARAM = 'rikiavimas';
export const URL_FILTER_PARAM = 'filtrai';

export const FILTER_CATEGORY = 'filterCategory';

export const MONTHLY_PRICE_FIELD_NAME = 'monthlyPrice';
export const FULL_PRICE_FIELD_NAME = 'fullPrice';
export const PLAN_PRICE_FIELD_NAME = 'price';
export const PLAN_DATA_SIZE_FIELDS = [
    'dataSizeDomesticTotal',
    'dataSizeEuropeTotal',
    'dataSizeTravelDestinationTotal',
    'dataSizeDomesticStandard',
] as const;

export const FILTER_CHIP_PREFIXES: { [key in (typeof PLAN_DATA_SIZE_FIELDS)[number]]?: string } = {
    dataSizeDomesticTotal: 'search.component.selected_filters.data_size_prefix.domestic',
    dataSizeDomesticStandard: 'search.component.selected_filters.data_size_prefix.domestic',
    dataSizeEuropeTotal: 'search.component.selected_filters.data_size_prefix.europe',
};

export const FILTER_VALUES_WITH_LABELS = ['Taip', 'Ne'];

export const FILTERS_WITH_AND_COMBINATION = ['specialCharacteristics'];
export const SKIP_FACETS_FOR_FIELDS = ['categoryPath'];

export const SEARCH_FILTERABLE_FIELDS: Record<string, 'terms' | 'range' | 'hierarchy'> = {
    ai: 'terms',
    baseCategory: 'terms',
    brand: 'terms',
    builtInSpeakers: 'terms',
    cameraResolution: 'terms',
    categories: 'hierarchy',
    category: 'terms',
    categoryPath: 'terms',
    categoryPriorityIndex: 'range',
    code: 'terms',
    color: 'terms',
    compatibility: 'terms',
    connectionTechnology: 'terms',
    connectivityType: 'terms',
    cpu: 'terms',
    delayTime: 'terms',
    drivingRange: 'terms',
    effectiveDate: 'terms',
    exclude: 'terms',
    fabric: 'terms',
    fullPrice: 'range',
    graphicCard: 'terms',
    hardDisk: 'terms',
    id: 'terms',
    integratedBattery: 'terms',
    internalMemory: 'terms',
    internetConnectivity: 'terms',
    is5gSupported: 'terms',
    isSmartTv: 'terms',
    leasingInitialAmount: 'range',
    ledLighting: 'terms',
    memory: 'terms',
    memoryType: 'terms',
    modemCharacteristic: 'terms',
    monthlyPrice: 'range',
    motionSensorAlarm: 'terms',
    name: 'terms',
    noiseCancellation: 'terms',
    oldFullPrice: 'range',
    operatingSystem: 'terms',
    ram: 'terms',
    refreshRate: 'terms',
    residualValue: 'range',
    scalaId: 'terms',
    screenResolution: 'terms',
    screenSize: 'terms',
    screenType: 'terms',
    series: 'terms',
    shellType: 'terms',
    simTypes: 'terms',
    smartHomeConnectivity: 'terms',
    specialCharacteristics: 'terms',
    specialOffers: 'terms',
    stockStatus: 'terms',
    sustainability: 'terms',
    technology: 'terms',
    teliaApp: 'terms',
    useCaseType: 'terms',
    variantCode: 'terms',
    videoTransmission: 'terms',
    warranty: 'terms',
    waterResistance: 'terms',
    weight: 'terms',
    weightGrams: 'range',
    withBluRay: 'terms',
    withDVD: 'terms',
    model: 'terms',
};

export const CATEGORY_PRODUCTS_CIRCUIT_BREAKER_REQUEST_TIMEOUT = 5000; // time to wait for response - 5 seconds
export const CATEGORY_PRODUCTS_CIRCUIT_BREAKER_ERROR_THRESHOLD_PERCENTAGE = 50; // percentage when to open CB
export const CATEGORY_PRODUCTS_CIRCUIT_BREAKER_RESET_TIMEOUT = 60000; // time until switching CB to half open - 60 seconds
